import { createTheme } from '@mui/material/styles';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#60A5FA', // Lighter blue for dark mode
      light: '#93C5FD',
      dark: '#3B82F6',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#34D399', // Lighter emerald green
      light: '#6EE7B7',
      dark: '#10B981',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0F172A', // Dark slate background
      paper: '#1E293B', // Darker slate for cards and papers
    },
    surface: {
      main: '#334155', // Medium slate
      light: '#475569',
      dark: '#1E293B',
    },
    text: {
      primary: '#F1F5F9', // Light text
      secondary: '#CBD5E1', // Medium light gray text
    },
    divider: '#334155',
    error: {
      main: '#F87171',
    },
    success: {
      main: '#34D399',
    },
    warning: {
      main: '#FBBF24',
    },
    info: {
      main: '#60A5FA',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0F172A',
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(96, 165, 250, 0.1) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(52, 211, 153, 0.1) 0%, transparent 50%)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E293B',
          borderBottom: '1px solid #334155',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E293B',
          borderRight: '1px solid #334155',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          },
          // Global cancel button styling using data attribute
          '&[data-cancel="true"]': {
            backgroundColor: '#6B7280',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#9CA3AF',
            },
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            boxShadow: '0 8px 25px rgba(96, 165, 250, 0.4)',
          },
          // Override for cancel buttons even when contained
          '&[data-cancel="true"]': {
            background: '#6B7280',
            color: '#FFFFFF',
            '&:hover': {
              background: '#9CA3AF',
            },
          },
        },
        outlined: {
          borderColor: '#475569',
          color: '#CBD5E1',
          '&:hover': {
            borderColor: '#60A5FA',
            backgroundColor: 'rgba(96, 165, 250, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1E293B',
          border: '1px solid #334155',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#1E293B',
          border: '1px solid #334155',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: '#334155',
          color: '#CBD5E1',
          '&.MuiChip-colorPrimary': {
            backgroundColor: 'rgba(96, 165, 250, 0.2)',
            color: '#93C5FD',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: 'rgba(52, 211, 153, 0.2)',
            color: '#6EE7B7',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#1E293B',
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#475569',
            },
            '&:hover fieldset': {
              borderColor: '#60A5FA',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#60A5FA',
            },
            '& input': {
              color: '#F1F5F9',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#CBD5E1',
            '&.Mui-focused': {
              color: '#60A5FA',
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(96, 165, 250, 0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(96, 165, 250, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(96, 165, 250, 0.3)',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: '#94A3B8',
          '&.Mui-selected': {
            color: '#60A5FA',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#60A5FA',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.025em',
      color: '#F1F5F9',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.025em',
      color: '#F1F5F9',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.025em',
      color: '#F1F5F9',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.025em',
      color: '#F1F5F9',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.025em',
      color: '#F1F5F9',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '-0.025em',
      color: '#F1F5F9',
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
      color: '#F1F5F9',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: '#CBD5E1',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '0.025em',
    },
  },
  shape: {
    borderRadius: 8,
  },
});

export default darkTheme; 